import { mapQuadToView } from './geometry';
const DEFAULT_GUIDE = [
    { x: 0.15, y: 0.22 },
    { x: 0.85, y: 0.22 },
    { x: 0.85, y: 0.78 },
    { x: 0.15, y: 0.78 },
];
export class QuadOverlay {
    constructor(canvas, options = {}) {
        this.sessionId = null;
        this.frame = { width: 720, height: 1280 };
        this.lastDetectedAt = -Infinity;
        this.rafId = null;
        this.canvas = canvas;
        this.smoothing = options.smoothing ?? 0.28;
        this.revertMs = options.revertMs ?? 600;
        this.guide = options.guide ?? DEFAULT_GUIDE;
        this.fit = options.fit ?? 'cover';
        this.cornerRadius = options.cornerRadius ?? 16;
        this.strokeStyle = options.strokeStyle ?? 'rgba(255,255,255,0.95)';
        this.lineWidth = options.lineWidth ?? 3;
        this.raf =
            options.raf ?? ((cb) => globalThis.requestAnimationFrame(cb));
        this.caf = options.caf ?? ((id) => globalThis.cancelAnimationFrame(id));
        this.now = options.now ?? (() => performance.now());
        this.current = cloneQuad(this.guide);
        this.target = cloneQuad(this.guide);
    }
    /** startScan 결과의 sessionId 를 설정 — 이후 다른 세션 이벤트는 무시된다 */
    setSession(sessionId) {
        this.sessionId = sessionId;
        this.lastDetectedAt = -Infinity;
    }
    /** detection 이벤트를 그대로 전달 */
    update(ev) {
        if (this.sessionId !== null && ev.sessionId !== this.sessionId)
            return; // T5 잔류 이벤트 폐기
        this.frame = { width: ev.frameW, height: ev.frameH };
        if (ev.detected && ev.quads.length > 0) {
            this.target = cloneQuad(ev.quads[0].corners);
            this.lastDetectedAt = this.now();
        }
    }
    start() {
        if (this.rafId !== null)
            return;
        const tick = () => {
            this.step();
            this.render();
            this.rafId = this.raf(tick);
        };
        this.rafId = this.raf(tick);
    }
    stop() {
        if (this.rafId !== null) {
            this.caf(this.rafId);
            this.rafId = null;
        }
    }
    /** 1 프레임 진행 (테스트에서 직접 호출 가능) */
    step() {
        if (this.now() - this.lastDetectedAt > this.revertMs) {
            this.target = cloneQuad(this.guide);
        }
        for (let i = 0; i < 4; i++) {
            this.current[i] = lerpPt(this.current[i], this.target[i], this.smoothing);
        }
    }
    /** 현재 보간 상태 (정규화) — 테스트/외부 렌더러용 */
    getCurrent() {
        return cloneQuad(this.current);
    }
    render() {
        const ctx = this.canvas.getContext('2d');
        if (!ctx)
            return;
        const view = { width: this.canvas.width, height: this.canvas.height };
        if (view.width === 0 || view.height === 0)
            return;
        ctx.clearRect(0, 0, view.width, view.height);
        const pts = mapQuadToView(this.current, this.frame, view, this.fit);
        ctx.strokeStyle = this.strokeStyle;
        ctx.lineWidth = this.lineWidth;
        ctx.beginPath();
        roundedQuadPath(ctx, pts, this.cornerRadius);
        ctx.stroke();
    }
}
function cloneQuad(q) {
    return [{ ...q[0] }, { ...q[1] }, { ...q[2] }, { ...q[3] }];
}
function lerpPt(a, b, t) {
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}
function roundedQuadPath(ctx, pts, radius) {
    for (let i = 0; i < 4; i++) {
        const prev = pts[(i + 3) % 4];
        const cur = pts[i];
        const next = pts[(i + 1) % 4];
        const inA = movePoint(cur, prev, radius);
        const outA = movePoint(cur, next, radius);
        if (i === 0)
            ctx.moveTo(inA.x, inA.y);
        else
            ctx.lineTo(inA.x, inA.y);
        ctx.quadraticCurveTo(cur.x, cur.y, outA.x, outA.y);
    }
    ctx.closePath();
}
/** from 에서 to 방향으로 dist 만큼 이동한 점 (변 길이 절반으로 클램프) */
function movePoint(from, to, dist) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.hypot(dx, dy);
    if (len === 0)
        return { ...from };
    const d = Math.min(dist, len / 2);
    return { x: from.x + (dx / len) * d, y: from.y + (dy / len) * d };
}
//# sourceMappingURL=overlay.js.map